import React from "react";
import { Card } from "@/components/ui/card";
import { FileText, MoreVertical, Layers, ArrowUp, ArrowDown, Folder as FolderIcon, FolderOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import { useData } from "../DataContext";
import { List as ListEntity } from "@/entities/all";
import type { List } from "@/types";

interface ListCardProps {
  list: List;
  onMove: (direction: 'up' | 'down') => void;
  index: number;
  totalItems: number;
}

export default function ListCard({ list, onMove, index, totalItems }: ListCardProps) {
  const { folders, lists, sections, listItems, runOptimisticUpdate } = useData();

  const handleMoveToFolder = async (folderId: string) => {
    const originalState = { lists: [...lists] };
    const task = async () => ListEntity.update(list.id, { folder_id: folderId });
    const newState = {
      folders: [...folders],
      lists: lists.map(l => 
        l.id === list.id ? { ...l, folder_id: folderId } : l
      ),
      sections: [...sections],
      listItems: [...listItems]
    };
    runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
  };

  const handleRemoveFromFolder = async () => {
    const originalState = { lists: [...lists] };
    const task = async () => ListEntity.update(list.id, { folder_id: null });
    const newState = {
      folders: [...folders],
      lists: lists.map(l => 
        l.id === list.id ? { ...l, folder_id: null } : l
      ),
      sections: [...sections],
      listItems: [...listItems]
    };
    runOptimisticUpdate(task, { update: newState, originalState: { folders, ...originalState, sections, listItems } });
  };

  return (
    <motion.div
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      layout
    >
      <Card className="bg-white border-0 shadow-lg hover:shadow-xl transition-all duration-200 overflow-hidden group">
        <div className="flex items-start justify-between p-6">
          <Link to={createPageUrl(`EditList?id=${list.id}`)} className="flex-1">
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-slate-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-lg">
                <FileText className="w-6 h-6 text-white" />
              </div>
            </div>
            
            <h3 className="font-semibold text-slate-900 text-lg mb-3 line-clamp-2">
              {list.title}
            </h3>
            
            {list.inherited_list_ids?.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-blue-600 bg-blue-50 px-3 py-1.5 rounded-full mb-3 w-fit">
                <Layers className="w-3 h-3" />
                <span>Inherits {list.inherited_list_ids.length} list{list.inherited_list_ids.length > 1 ? 's' : ''}</span>
              </div>
            )}
            
            <div className="text-sm text-slate-500">
              <span>Tap to edit</span>
            </div>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button 
                className="p-2 hover:bg-slate-50 rounded-lg -mr-2"
                onClick={(e) => e.preventDefault()}
              >
                <MoreVertical className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onMove('up')} disabled={index === 0}>
                <ArrowUp className="w-4 h-4 mr-2" />
                Move Up
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onMove('down')} disabled={index === totalItems - 1}>
                <ArrowDown className="w-4 h-4 mr-2" />
                Move Down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              
              {/* Folder Management */}
              {list.folder_id ? (
                <DropdownMenuItem onClick={handleRemoveFromFolder}>
                  <FolderOpen className="w-4 h-4 mr-2" />
                  Remove from Folder
                </DropdownMenuItem>
              ) : (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <FolderIcon className="w-4 h-4 mr-2" />
                    Move to Folder
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {folders.length > 0 ? (
                      folders.map(folder => (
                        <DropdownMenuItem 
                          key={folder.id} 
                          onClick={() => handleMoveToFolder(folder.id)}
                        >
                          <FolderIcon className="w-4 h-4 mr-2" />
                          {folder.name}
                        </DropdownMenuItem>
                      ))
                    ) : (
                      <DropdownMenuItem disabled>
                        No folders available
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </Card>
    </motion.div>
  );
}
